import {EventEmitter, Injectable, Injector} from '@angular/core';
import {NavigationService} from '../core/navigation/navigation.service';
import {RequestsService} from '../core/requests/requests.service';
import {DiagramJsonHandlerService} from './diagram-json-handler.service';
import {ResourceCommands} from '../commands/resource/resource.commands';
import {MapTreeService} from '../core/services/map-tree.service';
import {ChangesTabStoreService} from '../editor-interface/sidebar/activity-sidebar/changes-tab/changes-tab-store.service';
import {BehaviorSubject, Observable} from 'rxjs';
import {ModeDataService} from '../editor-state/mode-data.service';
import {RequestTask} from '../shared/models/request-task.interface';

@Injectable()
export class DiagramUpdateService {
  actions = [];
  reqTimer = null;
  withRefresh = false;

  private readyForUpdateSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  readyForUpdate$: Observable<boolean> = this.readyForUpdateSubject.asObservable();

  constructor(
    private reqSvc: RequestsService,
    private navSvc: NavigationService,
    private diagramJsonSvc: DiagramJsonHandlerService,
    private mapTreeSvc: MapTreeService,
    private changesStore: ChangesTabStoreService,
    private modeDataSvc: ModeDataService,
    private injector: Injector
  ) {}

  public injectIntoResourceCommands() {
    const resourceCommands = this.injector.get(ResourceCommands);

    /** Injection **/
    resourceCommands.addService<DiagramUpdateService>('serviceObj.diagramUpdateSvc', this);
  }

  public group() {
    return new UpdateGroup(this.update.bind(this));
  }

  public update(
    data: any,
    skipAccum?: boolean,
    noJson?: boolean,
    withRefresh?: boolean,
    cb?: EventEmitter<boolean>
  ) {
    if (withRefresh) {
      this.withRefresh = withRefresh;
    }

    if (!noJson) {
      this.diagramJsonSvc.update(data);
    }

    if (this.reqTimer) {
      clearTimeout(this.reqTimer);
      this.reqTimer = null;
    }

    if (skipAccum) {
      this.sendActions();

      return this.sendTask(data, true).then(() => {
        if (cb) {
          cb.emit(true);
        }
      });
    }

    this.actions.push(data);
    this.readyForUpdateSubject.next(false);
    const accumDelay = this.modeDataSvc.isViewMode() ? 250 : 1000;

    this.reqTimer = setTimeout(() => {
      this.reqTimer = null;
      this.sendActions().then(() => {
        if (cb) {
          cb.emit(true);
        }
      });
    }, accumDelay);

    return Promise.resolve();
  }

  /**
   * Sends any accumulated actions immediately and resolves once the request
   * queue has fully drained (capped, so a stalled queue can never block
   * navigation). Never rejects: permanent failures are surfaced to the user
   * by RequestsService.
   */
  public flush(): Promise<void> {
    let timer;
    const timeout = new Promise<void>(resolve => {
      timer = setTimeout(resolve, DiagramUpdateService.FLUSH_TIMEOUT_MS);
    });

    return Promise.race([this.drainQueue(), timeout]).finally(() => clearTimeout(timer));
  }

  private static readonly FLUSH_TIMEOUT_MS = 15000;

  private drainQueue(): Promise<void> {
    if (this.reqTimer) {
      clearTimeout(this.reqTimer);
      this.reqTimer = null;
    }

    const pending = this.actions.length ? this.sendActions() : Promise.resolve();

    return Promise.resolve(pending)
      .catch(() => undefined)
      .then(() => this.reqSvc.whenIdle())
      .then(() => {
        if (this.actions.length || this.reqTimer) {
          return this.drainQueue();
        }
      });
  }

  private sendActions() {
    if (this.actions.length) {
      const actionsCopy = this.actions.slice();

      this.actions.length = 0;

      return this.sendTask({actions: actionsCopy}, this.withRefresh).then(result => {
        if (this.withRefresh) {
          this.makeRefresh();
        }

        this.changesStore.set('actions', actionsCopy);

        return result;
      });
    }
  }

  private sendTask(data: any, needResult?: boolean) {
    const currentNav = this.navSvc.getCurrent();
    const isViewMode = this.modeDataSvc.isViewMode();
    let url = `/diagram/${currentNav.id}/state/${currentNav.state}`;

    const stickyNotesActions = ['createNote', 'changeNote', 'deleteNote'];

    if (isViewMode) {
      const isStickyNoteAction = stickyNotesActions.some(
        name =>
          data?.actions?.[0]?.hasOwnProperty(name) ||
          data?.actions?.[0]?.group?.[0]?.hasOwnProperty(name)
      );

      if (isStickyNoteAction) {
        url += `/notes`;
      }
    }

    const task: RequestTask = {
      isApi: true,
      url: url,
      data: data,
      method: 'PUT'
    };

    return this.reqSvc.addToQueue(task, needResult).then(res => {
      this.readyForUpdateSubject.next(true);

      return res;
    });
  }

  private makeRefresh() {
    this.withRefresh = false;
    this.mapTreeSvc.triggerUpdate();
  }
}

export class UpdateGroup {
  actions = [];
  updateFn;

  constructor(updateFn) {
    this.updateFn = updateFn;
  }

  add(action) {
    this.actions.push(action);
  }

  count() {
    return this.actions.length;
  }

  getActions() {
    return this.actions;
  }

  sendGroup(skipAccum?) {
    return this.updateFn({group: this.actions}, skipAccum);
  }
}
